/**
 * Validation Engine — row-level sanitization & coercion.
 *
 * Takes parsed rows + a mapping table and produces a typed payload per row,
 * with per-row reasons when something is off. NEVER throws — all failures
 * surface in the row's `errors` array, so one bad row never breaks the file.
 *
 * Key responsibilities beyond schema enforcement:
 *   - Auto-detect numeric format (US vs EU) per source column.
 *   - Strip unit suffixes ("6.895,70 km" → 6895.70).
 *   - Tolerate many date formats (ISO, "YYYY-MM-DD HH:MM:SS", "DD/MM/YYYY",
 *     "MM/DD/YYYY", Excel serials, "Jun 7 2026", "2026-06-07T00:37:39Z").
 *   - Derive `distance` from odometer delta when distance is not directly
 *     mapped (configured per-field in schemas.ts).
 */
import type {
  FieldSpec,
  MappingTable,
  ParseResult,
  RequiredFieldSchema,
  RowValidation,
  ValidationResult,
} from "./types";

// ---------------- Number coercion ----------------

/**
 * Detect whether a column's numeric format is European (comma decimal,
 * period thousands) by sampling up to 25 non-empty values. Returns true
 * iff EU is strictly more likely than US.
 */
const detectEuFormat = (samples: string[]): boolean => {
  let euScore = 0;
  let usScore = 0;
  for (const raw of samples) {
    const s = String(raw).trim();
    if (!s) continue;
    // Patterns where decimal separator is unambiguous.
    // EU: "1.234,56" or "1234,56"
    if (/^-?\d{1,3}(\.\d{3})+,\d+/.test(s)) euScore += 2;
    else if (/^-?\d+,\d{1,4}$/.test(s) && !/^-?\d+,\d{3}$/.test(s)) euScore += 1;
    // US: "1,234.56" or "1234.56"
    if (/^-?\d{1,3}(,\d{3})+\.\d+/.test(s)) usScore += 2;
    else if (/^-?\d+\.\d{1,4}$/.test(s)) usScore += 1;
  }
  return euScore > usScore;
};

/** Coerce a raw cell to a finite number, or null when unparseable. */
const toNumber = (raw: string, euFormat: boolean): number | null => {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  // Strip common unit suffixes (km, mi, kwh, l, gal, %, $, etc.).
  s = s.replace(/(km|mi|mile|miles|kwh|wh|l|gal|gallons|liters|litres|%|\$|€|£|¥)\b/gi, "").trim();
  if (!s) return null;
  if (euFormat) {
    // Remove thousand-separator dots, swap decimal comma → dot.
    s = s.replace(/\./g, "").replace(/,/g, ".");
  } else {
    // Remove thousand-separator commas.
    s = s.replace(/,/g, "");
  }
  // Tolerate trailing/leading +.
  s = s.replace(/^\+/, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// ---------------- Date coercion ----------------

const DATE_FORMATS = [
  /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY or DD/MM/YYYY (ambiguous)
  /^(\d{1,2})-(\d{1,2})-(\d{4})$/, // DD-MM-YYYY or MM-DD-YYYY
  /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, // YYYY/MM/DD
];

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Best-effort ISO date (YYYY-MM-DD) extractor. Returns null when no
 * recognizable date is present. Supports:
 *   - ISO + timestamps ("2026-06-07T00:37:39Z", "2026-06-07 00:37:39")
 *   - Excel serial numbers (days since 1899-12-30)
 *   - Several human formats
 */
export const toIsoDate = (raw: string): string | null => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // ISO with time → keep date portion.
  const isoTime = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[T\s]/);
  if (isoTime) {
    const y = +isoTime[1]; const m = +isoTime[2]; const d = +isoTime[3];
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  // Pure ISO date.
  const iso = s.match(DATE_FORMATS[0]);
  if (iso) {
    const y = +iso[1]; const m = +iso[2]; const d = +iso[3];
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  // Excel serial number.
  if (/^\d{4,6}(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n) && n > 20000 && n < 80000) {
      // Excel base = 1899-12-30 (accounts for the 1900 leap bug).
      const ms = (n - 25569) * 86400 * 1000;
      const dt = new Date(ms);
      if (!Number.isNaN(dt.getTime())) {
        return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
      }
    }
  }

  // Slash / dash dates: prefer DD/MM when the first part is > 12.
  const slash = s.match(DATE_FORMATS[1]) || s.match(DATE_FORMATS[2]);
  if (slash) {
    const a = +slash[1]; const b = +slash[2]; const y = +slash[3];
    let mo: number; let da: number;
    if (a > 12) { da = a; mo = b; }
    else if (b > 12) { mo = a; da = b; }
    else { mo = a; da = b; } // ambiguous — assume MM/DD (US default)
    if (mo >= 1 && mo <= 12 && da >= 1 && da <= 31) return `${y}-${pad2(mo)}-${pad2(da)}`;
  }
  const ydm = s.match(DATE_FORMATS[3]);
  if (ydm) {
    const y = +ydm[1]; const m = +ydm[2]; const d = +ydm[3];
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  // Last-resort parse via Date — beware of timezone shifts; we use UTC parts.
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  }
  return null;
};

// ---------------- Validation orchestrator ----------------

interface ValidateOptions {
  schema: RequiredFieldSchema;
  parsed: ParseResult;
  mapping: MappingTable;
}

export function validate({ schema, parsed, mapping }: ValidateOptions): ValidationResult {
  // 1. Detect EU vs US numeric format per mapped source column once,
  //    using the first ~25 non-empty samples. Drastically improves accuracy
  //    over per-cell guessing.
  const formatByHeader = new Map<string, boolean>();
  for (const fieldKey of Object.keys(mapping)) {
    const m = mapping[fieldKey];
    if (!m.header) continue;
    const fs = schema.fields.find((f) => f.key === fieldKey);
    if (!fs || (fs.type !== "number" && fs.type !== "percent")) continue;
    const samples: string[] = [];
    for (const row of parsed.rows) {
      const v = row[m.header];
      if (v && samples.length < 25) samples.push(v);
    }
    formatByHeader.set(m.header, detectEuFormat(samples));
  }

  const allRows: RowValidation[] = parsed.rows.map((row, idx) => {
    const rowNumber = idx + 2; // header is row 1
    const values: Record<string, unknown> = {};
    const errors: string[] = [];

    // Coerce every mapped field.
    for (const fs of schema.fields) {
      const m = mapping[fs.key];
      if (!m) continue;
      // Derived fields handled after the main pass.
      if (m.confidence === "derived") continue;
      if (!m.header) {
        if (fs.required) errors.push(`missing ${fs.label.toLowerCase()}`);
        continue;
      }
      const raw = row[m.header] ?? "";
      const eu = formatByHeader.get(m.header) ?? false;
      const coerced = coerce(fs, raw, eu);
      if (coerced.error) {
        // Only fail HARD when the field is required (or when an optional
        // numeric/date was clearly provided but unparseable — silent skip
        // would corrupt downstream math).
        if (fs.required || raw) errors.push(`${fs.label.toLowerCase()}: ${coerced.error}`);
        continue;
      }
      values[fs.key] = coerced.value;
    }

    // Derivation pass — currently only `distance` from odometer delta.
    for (const fs of schema.fields) {
      const m = mapping[fs.key];
      if (!m || m.confidence !== "derived") continue;
      if (fs.key === "distance" && fs.derive?.from.length === 2) {
        const startKm = values["start_odometer"] as number | undefined;
        const endKm = values["end_odometer"] as number | undefined;
        if (typeof startKm === "number" && typeof endKm === "number") {
          const delta = endKm - startKm;
          if (delta < 0) {
            errors.push("distance: end odometer is less than start");
          } else if (delta === 0) {
            // Zero-distance rows are common in EV logs (stationary "drives"
            // when the car wakes briefly). Skip them silently, matching the
            // existing backend behavior.
            errors.push("zero distance (skipped)");
          } else {
            values["distance"] = delta;
            values["unit"] = "km"; // odometer is always km in our schema
          }
        } else if (fs.required) {
          errors.push("distance: could not derive from odometers");
        }
      }
    }

    return { rowNumber, values, errors };
  });

  const validRows = allRows.filter((r) => r.errors.length === 0);
  const invalidRows = allRows.filter((r) => r.errors.length > 0);
  return { allRows, validRows, invalidRows };
}

interface CoerceOutcome {
  value: unknown;
  error: string | null;
}

const coerce = (fs: FieldSpec, raw: string, eu: boolean): CoerceOutcome => {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return { value: undefined, error: fs.required ? "empty" : null };
  switch (fs.type) {
    case "date": {
      const iso = toIsoDate(trimmed);
      if (!iso) return { value: undefined, error: `invalid date '${trimmed}'` };
      return { value: iso, error: null };
    }
    case "number": {
      const n = toNumber(trimmed, eu);
      if (n == null) return { value: undefined, error: `invalid number '${trimmed}'` };
      return { value: n, error: null };
    }
    case "percent": {
      let n = toNumber(trimmed, eu);
      if (n == null) return { value: undefined, error: `invalid percent '${trimmed}'` };
      // Fractional inputs ("0.74" → 74%).
      if (n > 0 && n <= 1) n = n * 100;
      if (n < 0 || n > 100) return { value: undefined, error: `percent out of range: ${n}` };
      return { value: n, error: null };
    }
    case "string":
    default:
      return { value: trimmed, error: null };
  }
};
